import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Card, Container, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { subscriptionAPI } from '../utils/api';
import type { Subscription } from '../types/billing';

const statusVariant: Record<Subscription['status'], string> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  cancelled: 'secondary',
};

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format((amount || 0) / 100);

const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString() : '-';

const Subscriptions: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'subscriptions' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canViewBilling } = useRolePermissions();
  const nurseryId = currentNursery?.nursery_id || '';
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const subscriptionResponse = await subscriptionAPI.list(nurseryId);
      setSubscriptions(subscriptionResponse.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!canViewBilling) {
    return <Layout><Container fluid className="py-4"><Alert variant="danger">{t('noAccess')}</Alert></Container></Layout>;
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div><h2 className="fw-bold text-primary mb-0">{t('title')}</h2></div>
        </div>
        {currentNursery ? (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: tc('nav.subscriptions').toLowerCase() })}</strong> {currentNursery.description || currentNursery.name} ({tc('labels.id')}: {currentNursery.nursery_id})
          </Alert>
        ) : (
          <Alert variant="warning">{t('selectNursery')}</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light"><tr><th>{t('parent')}</th><th>{tc('labels.child')}</th><th>{tc('labels.status')}</th><th>{t('amount')}</th><th>{t('currentPeriod')}</th><th>{t('stripeId')}</th><th className="text-end">{tc('labels.actions')}</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-5"><Spinner animation="border" /></td></tr> : subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>{subscription.parent_name}</td>
                    <td>{subscription.child_name}</td>
                    <td><Badge bg={statusVariant[subscription.status]}>{t(`statuses.${subscription.status}`, { defaultValue: subscription.status.replace('_', ' ') })}</Badge></td>
                    <td>{formatMoney(subscription.amount, subscription.currency)}</td>
                    <td>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</td>
                    <td><code>{subscription.stripe_subscription_id}</code></td>
                    <td className="text-end text-muted">-</td>
                  </tr>
                ))}
                {!loading && subscriptions.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-5">{t('noSubscriptions')}</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default Subscriptions;
